import {Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {Store} from "@ngrx/store";
import {Observable, startWith} from "rxjs";
import {NumberColumnComponent} from "../../../shared/shared/table/components/number-column/number-column.component";
import {ITablePagerOptions} from "../../../shared/shared/table/components/table-pager/ITablePagerOptions";
import {ITableColumn} from "../../../shared/shared/table/table-column";
import {
    IAddPlaybookDTO,
    IPlaybookCategoryDTO,
    IPlaybooksItemDTO,
    IUpdateGuidelineDTO,
    IUpdatePlaybookGuidelineDTO
} from "../dto/playbooks-listening.dto";
import {
    PlaybookStatusColumnComponent
} from "../legal-hub-playbooks-listening/columns/playbook-status-column/playbook-status-column.component";
import {
    IPlaybookDefaultFilter
} from "../legal-hub-playbooks-listening/legal-hub-basic-filters/legal-hub-basic-filters.component";
import * as PlaybooksActions from './playbooks.actions';
import * as PlaybooksListingSelectors from "./playbooks.selectors";
import {Router} from '@angular/router';
import {filter, map} from "rxjs/operators";
import { PlaybookNameComponent } from '../legal-hub-playbooks-listening/columns/playbook-name/playbook-name.component';
import { PlaybookUsageComponent } from '../legal-hub-playbooks-listening/columns/playbook-usage/playbook-usage.component';
import { PlaybookCategoryComponent } from '../legal-hub-playbooks-listening/columns/playbook-category/playbook-category.component';
import { PlaybookOwnerComponent } from '../legal-hub-playbooks-listening/columns/playbook-owner/playbook-owner.component';
import { PlaybookGuidelinesComponent } from '../legal-hub-playbooks-listening/columns/playbook-guidelines/playbook-guidelines.component';
import { PlaybookLastEditedComponent } from '../legal-hub-playbooks-listening/columns/playbook-last-edited/playbook-last-edited.component';
import Swal from 'sweetalert2';
import { PlaybookListingActionColumnComponent } from '../legal-hub-playbooks-listening/columns/playbook-listing-action-column/playbook-listing-action-column.component';


@Injectable({
    providedIn: 'root'
})
export class PlaybooksListeningFacadeService {

    playbooksList$: Observable<IPlaybooksItemDTO[]> = this._store.select(PlaybooksListingSelectors.playbooks);
    search$: Observable<string> = this._store.select(PlaybooksListingSelectors.search);
    paging$: Observable<ITablePagerOptions> = this._store.select(PlaybooksListingSelectors.paging);
    isLoading$: Observable<boolean> = this._store.select(PlaybooksListingSelectors.isLoading)
    defaultFilters$ = this._store.select(PlaybooksListingSelectors.defaultFilters);
    columns$: Observable<ITableColumn[]>;

    isCategoriesLoading$: Observable<boolean> = this._store.select(PlaybooksListingSelectors.isCategoriesLoading);
    categoriesList$: Observable<IPlaybookCategoryDTO[]> = this._store.select(PlaybooksListingSelectors.categories);

    constructor(private _store: Store,
                private _translocoService: TranslocoService,
                private _router: Router
            ) {
        
        this.columns$ = this._translocoService.events$
                .pipe(
                    startWith({
                        type: 'translationLoadSuccess'
                    }),
                    filter(e => e.type === 'translationLoadSuccess' || e.type ==='langChanged'),
                    map(() => {
                        return this.setupColumns();
                    }),
                );
    }

    fetchPlaybooks() {
        this._store.dispatch(PlaybooksActions.fetchPlaybooks());
    }

    fetchCategories(search?:string) {
        this._store.dispatch(PlaybooksActions.fetchCategories({search}));
    }
    
    addPlaybook(newPlaybook:IAddPlaybookDTO){
        this._store.dispatch(PlaybooksActions.addNewPlaybook(newPlaybook))
    }

    updatePlaybook(playbookId: number, updatedPlaybook: IUpdatePlaybookGuidelineDTO) {
        this._store.dispatch(PlaybooksActions.updatePlaybook(playbookId, updatedPlaybook));
    }

    updateGuideline(playbookId: number, guideline: IUpdateGuidelineDTO) {
        this._store.dispatch(PlaybooksActions.updateGuideline(playbookId, guideline));
    }


    onSearchChange(searchValue: string) {
        this._store.dispatch(PlaybooksActions.searchChange(searchValue));
    }

    onDefaultFilterChange(filters: IPlaybookDefaultFilter){
        this._store.dispatch(PlaybooksActions.defaultFilterChanged(filters));
    }

    changePage(pageNumber: number): void {
        this._store.dispatch(PlaybooksActions.changePage(pageNumber));
    }

    addNewCategory(categoryName:string):void{
        this._store.dispatch(PlaybooksActions.addNewCategory(categoryName));
    }

   editPlaybookClicked(playbook: IPlaybooksItemDTO):void {
        if(playbook){
            this._store.dispatch(PlaybooksActions.editPlaybookClicked({playbook}));
        }
    }


    deletePlaybook(playbookId: number):void {
        this._store.dispatch(PlaybooksActions.deletePlaybook(playbookId));
    }


    addNewPlaybookClicked(){
        this._store.dispatch(PlaybooksActions.addNewPlaybookClicked());
    }

    resetState(){
        this._store.dispatch(PlaybooksActions.resetState());
    }


    async confirmAndDeletePlaybook(dataItemId: number) {
      const { isConfirmed } = await this.confirmDeleteDocumentModal(
        this._translocoService.translate('Ask to delete playbook (Legalhub Section) - Do you want to delete this playbook permanently')
      );
      if (isConfirmed) {
        this.deletePlaybook(dataItemId);
      }
    }



   /**
    * Open a confirmation modal to delete a playbook
    * @param confirmationText - text to show in the confirmation modal
    * @returns Promise with the result of the modal
    */
    async confirmDeleteDocumentModal(confirmationText: string): Promise<import("sweetalert2").SweetAlertResult<unknown>> {
        return await Swal.fire({
            title: this._translocoService.translate('User confirmation (general) - Are you sure'),
            text: confirmationText,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: this._translocoService.translate('Modal button (general) - Yes'),
            confirmButtonColor: '#ef0016',
            cancelButtonText: this._translocoService.translate('Modal button (general) - No'),
        });
    }

    private setupColumns(): ITableColumn[] {
        return [
            {
                field: 'name',
                title: this._translocoService.translate('Playbook column name  (legal hub playbooks listening) - Playbook Name'),
                cell: PlaybookNameComponent,
                cellCssClass:'align-middle'
            },
            {
                field: 'usage',
                title: this._translocoService.translate('Usage column name  (legal hub playbooks listening) - Usage'),
                cell: PlaybookUsageComponent,
                cellCssClass:'align-middle'
            },
            {
                field: 'playbook_category.name',
                title: this._translocoService.translate('Category column name  (legal hub playbooks listening) - Category'),
                cell: PlaybookCategoryComponent,
                cellCssClass:'align-middle'
            },
            {
                field: 'playbook_guidelines_count',
                title: this._translocoService.translate('Guidelines column name  (legal hub playbooks listening) - Guidelines'),
                cell: PlaybookGuidelinesComponent,
                cellCssClass:'align-middle'
            },
            {
                field: 'user.name',
                title: this._translocoService.translate('Owner column name  (legal hub playbooks listening) - Owner'),
                cell: PlaybookOwnerComponent,
                cellCssClass:'align-middle'
            },
            {
                field: 'updated_at',
                cell: PlaybookLastEditedComponent,
                title: this._translocoService.translate('Last Edited column name  (legal hub playbooks listening) - Last Edited'),
                cellCssClass:'align-middle'
            },
            {
               title:'',
               cell: PlaybookListingActionColumnComponent,
               cellCssClass:'action-column pe-1 align-middle',
            }
        ]
    }
}
